import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { StyledPaperItem } from '../common/mui-components';
import { Maintenance } from '../components/room/types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  uniqueNamesGenerator,
  adjectives,
  animals,
} from 'unique-names-generator';
import { useNavigate } from 'react-router-dom';
import { Categories } from '../components/create-game/Categories';
import { resizeImage } from '../api/cloudinary';
import { fetchGet } from '@par/shared/api/common';
import { timeSince } from '@par/shared/utils/date-time';
import CasinoIcon from '@mui/icons-material/Casino';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LayersIcon from '@mui/icons-material/Layers';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import {
  Clue,
  Order,
  StarterKit,
  StarterKitConfig,
  StarterKitGamesAndStats,
  StarterKitUserStats,
} from '../components/starter-kits/types';
import {
  getClueLabels,
  sortByArrayCompare,
} from '../components/starter-kits/constants';
import { Line } from 'react-chartjs-2';
import { CreateGameConfig } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';

const nameGenConfig = {
  dictionaries: [adjectives, animals],
  separator: ' ',
  length: 2,
};

export type Leader = {
  createdAt: number;
  profileId: string;
  username: string;
  avatarPublicId: string;
  score: number;
  slain: number;
};

type PropTypes = {
  close: () => void;
  handleStarterKitPlay: () => void;
  maintenance: Maintenance;
  open: boolean;
  profile: Profile;
  setCreateGameConfig: (opts: CreateGameConfig) => void;
  starterKit: StarterKit;
  userStats?: StarterKitGamesAndStats[];
};

export const StarterKitDialog = ({
  close,
  handleStarterKitPlay,
  maintenance,
  open,
  profile,
  setCreateGameConfig,
  starterKit,
  userStats,
}: PropTypes) => {
  const ordersSorted = ['random', 'sequence', 'alphabetical', 'group'];
  const cluesSorted = ['1 only', '2 only', '1 then 2', '2 then 1'];
  starterKit.orders.sort((a, b) => sortByArrayCompare(ordersSorted, a, b));
  starterKit.clues.sort((a, b) => sortByArrayCompare(cluesSorted, a, b));
  const theme = useTheme();
  const navigate = useNavigate();
  const [leaders, setLeaders] = useState<Leader[]>();
  const [order, setOrder] = useState<Order>('random');
  const [clue, setClue] = useState<Clue>('1 only');
  const [leaderboardOrder, setLeaderboardOrder] = useState<Order>('random');
  const [leaderboardClue, setLeaderboardClue] = useState<Clue>('1 only');
  const [chartOrder, setChartOrder] = useState<Order>('random');
  const [chartClue, setChartClue] = useState<Clue>('1 only');
  const mdBreakpoint = useMediaQuery(theme.breakpoints.down('md'));

  // ChartJS.register(CategoryScale, BarElement, LinearScale);

  useEffect(() => {
    if (!open) return;
    updateGameConfig();
    fetchStarterKitLeaders();
  }, [open]);

  useEffect(() => {
    fetchStarterKitLeaders();
  }, [leaderboardOrder, leaderboardClue]);

  useEffect(() => {
    updateGameConfig();
  }, [order, clue]);

  const updateGameConfig = () => {
    const generatedcustomGameName = uniqueNamesGenerator({
      ...nameGenConfig,
      style: 'capital',
    });
    const updatedStarterKit: StarterKitConfig = {
      ...starterKit,
      order: order,
      clue: clue,
    };
    const gameOpts: CreateGameConfig = {
      allowSpectators: false,
      categorySet: null,
      createEmptyGame: false,
      customGameName: generatedcustomGameName,
      customSitAndGoMaxPlayers: 10,
      demo: false,
      demons: false,
      gameType: null,
      matchMaking: false,
      mode: 'solo',
      ranked: false,
      starterKit: updatedStarterKit,
      variant: 'starter-kit',
    };
    setCreateGameConfig(gameOpts);
  };

  const fetchStarterKitLeaders = async () => {
    const res: Leader[] = await fetchGet(
      `/starter-kits/leaders/${starterKit.starterKitSet}/${leaderboardOrder}/${leaderboardClue}`
    );
    if (res) {
      res.sort((a, b) => a.score - b.score);
    }
    setLeaders(res);
  };

  const handleOrderChange = (event: any, type: Order) => {
    if (type !== null) {
      setOrder(type);
    }
  };

  const handleClueChange = (event: any, type: Clue) => {
    if (type !== null) {
      setClue(type);
    }
  };

  const handleLeaderboardOrderToggle = (
    event: React.MouseEvent<HTMLElement>,
    nextView: Order
  ) => {
    if (nextView === null) return;
    setLeaderboardOrder(nextView);
  };

  const handleLeaderboardClueToggle = (
    event: React.MouseEvent<HTMLElement>,
    nextView: Clue
  ) => {
    if (nextView === null) return;
    setLeaderboardClue(nextView);
  };

  const handleChartOrderToggle = (
    event: React.MouseEvent<HTMLElement>,
    nextView: Order
  ) => {
    if (nextView === null) return;
    setChartOrder(nextView);
  };

  const handleChartClueToggle = (
    event: React.MouseEvent<HTMLElement>,
    nextView: Clue
  ) => {
    if (nextView === null) return;
    setChartClue(nextView);
  };

  const getGamesPlayed = () => {
    if (!userStats) return;
    let gamesCount = 0;
    for (const us of userStats) {
      gamesCount = gamesCount + us.games.length;
    }
    return gamesCount;
  };

  const getLineChart = () => {
    if (!userStats) return;
    const foundUserStat = userStats.find(
      (us) => us.order === chartOrder && us.clue === chartClue
    );
    const labels = foundUserStat?.games.map((g) =>
      timeSince(new Date(g.createdAt))
    );
    const scoresData = foundUserStat?.games.map((g) => g.score);
    const data = {
      labels: labels,
      datasets: [
        {
          data: scoresData,
          backgroundColor: theme.palette.ranked.main,
          borderColor: theme.palette.ranked.main,
          borderWidth: 1,
        },
      ],
    };
    const options = {
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          display: true,
        },
        y: {
          display: true,
        },
      },
      elements: {
        point: {
          radius: 2,
        },
      },
    };
    return <Line data={data} options={options} />;
  };

  const getPersonalTrackerTable = () => {
    if (!userStats) return;
    const getUserStat = (order: Order, clue: Clue) => {
      const found = userStats.find(
        (us) => us.order === order && us.clue === clue
      );
      if (!found) return '--';
      return `${found.bestScore.score > 0 ? '+' : ''}${found.bestScore.score}`;
    };
    return (
      <TableContainer component={Paper}>
        <Table size="small" aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              {starterKit.clues.map((c) => (
                <TableCell>{c}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {starterKit.orders.map((o) => (
              <TableRow>
                <TableCell>{o}</TableCell>
                {starterKit.clues.map((c) => (
                  <TableCell sx={{ textAlign: 'center', fontWeight: 600 }}>
                    {getUserStat(o, c)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const leaderboardData = (leaders: Leader[]) =>
    mdBreakpoint ? leaders?.slice(0, 50) : leaders?.slice(0, 25);

  const leaderboardTable = () => (
    <>
      <TableContainer component={Paper}>
        <Table stickyHeader size="small" aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell width={'8%'} sx={{ textAlign: 'center' }}>
                #
              </TableCell>
              <TableCell
                sx={{
                  maxWidth: '120px',
                }}
              >
                Player
              </TableCell>
              <TableCell width={'15%'} sx={{ textAlign: 'center' }}>
                Score
              </TableCell>
              <TableCell width={'25%'}>Played</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaderboardData(leaders)?.length ? (
              leaderboardData(leaders).map((l: Leader, index: number) => (
                <TableRow key={l.profileId}>
                  <TableCell sx={{ textAlign: 'center' }}>
                    {index + 1}
                  </TableCell>
                  <TableCell>
                    <Stack direction={'row'} spacing={2}>
                      <Avatar
                        src={resizeImage(l.avatarPublicId, 50, 50)}
                        variant="rounded"
                        sx={{
                          height: 25,
                          width: 25,
                        }}
                      >
                        {!l.avatarPublicId ? l.username.charAt(0) : null}
                      </Avatar>

                      <Typography
                        sx={{
                          lineHeight: '25px',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          maxWidth: '120px',
                        }}
                      >
                        {l.username}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    {l.score > 0 ? '+' : ''}
                    {l.score}
                  </TableCell>
                  <TableCell>{timeSince(new Date(l.createdAt))} ago</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={4}
                  sx={{
                    textAlign: 'center',
                    fontStyle: 'italic',
                  }}
                >
                  No leaders
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!mdBreakpoint ? (
        <>
          <Divider orientation="vertical" flexItem />
          <TableContainer component={Paper}>
            <Table stickyHeader size="small" aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell width={'8%'} sx={{ textAlign: 'center' }}>
                    #
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: '120px',
                    }}
                  >
                    Player
                  </TableCell>
                  <TableCell width={'15%'} sx={{ textAlign: 'center' }}>
                    Score
                  </TableCell>
                  <TableCell width={'25%'}>Played</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaders?.slice(25, 50).map((l: Leader, index: number) => (
                  <TableRow key={l.profileId}>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {index + 26}
                    </TableCell>
                    <TableCell>
                      <Stack direction={'row'} spacing={2}>
                        <Avatar
                          src={resizeImage(l.avatarPublicId, 50, 50)}
                          variant="rounded"
                          sx={{
                            height: 25,
                            width: 25,
                          }}
                        >
                          {!l.avatarPublicId ? l.username.charAt(0) : null}
                        </Avatar>
                        <Typography
                          sx={{
                            lineHeight: '25px',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            maxWidth: '120px',
                          }}
                        >
                          {l.username}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {l.score > 0 ? '+' : ''}
                      {l.score}
                    </TableCell>
                    <TableCell>
                      {timeSince(new Date(l.createdAt))} ago
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}
    </>
  );

  return (
    <Dialog open={open} onClose={close} maxWidth="lg">
      <DialogTitle>
        <Typography
          variant="h5"
          textAlign={'center'}
          letterSpacing={5}
          sx={{
            textShadow: `0 0 4px ${theme.palette.primary.main},
0 0 4px ${theme.palette.primary.main},
0px 0px 16px ${theme.palette.primary.main}`,
          }}
        >
          {starterKit.starterKitName.toUpperCase()}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ width: '100%', height: '100%' }}>
        <Grid container spacing={2} className="CONTAINER-MAIN">
          <Grid item xs={12} sm={12} className="PERSONAL-TRACKER">
            <StyledPaperItem>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Typography
                    width={'100%'}
                    textAlign={'center'}
                    fontWeight={600}
                    letterSpacing={4}
                  >
                    PERSONAL TRACKER
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {userStats ? (
                    <Stack spacing={2}>
                      <Stack
                        direction={'row'}
                        spacing={2}
                        width={'100%'}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <Stack
                          spacing={1}
                          sx={{
                            padding: 2,
                            border: '1px solid',
                            borderColor: theme.palette.info.main,
                            borderRadius: 5,
                          }}
                        >
                          <Typography
                            fontWeight={600}
                            sx={{
                              color: theme.palette.info.main,
                            }}
                            letterSpacing={2}
                          >
                            TOTAL GAMES PLAYED
                          </Typography>
                          <Typography
                            variant="h2"
                            fontWeight={600}
                            height={'100%'}
                            sx={{
                              textShadow: `0 0 4px ${theme.palette.info.main},
                              0 0 4px ${theme.palette.info.main},
                              0px 0px 16px ${theme.palette.info.main}`,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            {getGamesPlayed() || 0}
                          </Typography>
                        </Stack>
                        <Stack
                          sx={{
                            padding: 2,
                            border: '1px solid',
                            borderColor: theme.palette.primary.main,
                            borderRadius: 5,
                          }}
                        >
                          <Typography
                            fontWeight={600}
                            sx={{ color: theme.palette.primary.main }}
                            letterSpacing={2}
                          >
                            BEST SCORES
                          </Typography>
                          <Box
                            sx={{
                              height: '100%',
                              color: theme.palette.info.main,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            {getPersonalTrackerTable()}
                          </Box>
                        </Stack>
                      </Stack>
                      <Stack
                        spacing={2}
                        sx={{
                          padding: 2,
                          border: '1px solid',
                          borderColor: theme.palette.underPar.main,
                          borderRadius: 5,
                        }}
                      >
                        <Typography
                          fontWeight={600}
                          sx={{ color: theme.palette.underPar.main }}
                          letterSpacing={2}
                        >
                          PREVIOUS SCORES
                        </Typography>
                        <Stack
                          direction={'row'}
                          spacing={5}
                          sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <Stack spacing={2}>
                            <ToggleButtonGroup
                              orientation="vertical"
                              value={chartOrder}
                              exclusive
                              onChange={handleChartOrderToggle}
                              size="small"
                            >
                              {starterKit.orders?.map((o) => (
                                <ToggleButton value={o} aria-label={o}>
                                  <Tooltip title={o} placement="left">
                                    {o === 'random' ? (
                                      <CasinoIcon />
                                    ) : o === 'alphabetical' ? (
                                      <SortByAlphaIcon />
                                    ) : o === 'sequence' ? (
                                      <FormatListNumberedIcon />
                                    ) : o === 'group' ? (
                                      <LayersIcon />
                                    ) : null}
                                  </Tooltip>
                                </ToggleButton>
                              ))}
                            </ToggleButtonGroup>
                            <ToggleButtonGroup
                              orientation="vertical"
                              value={chartClue}
                              exclusive
                              onChange={handleChartClueToggle}
                              size="small"
                            >
                              {starterKit.clues?.map((c) => (
                                <ToggleButton value={c} aria-label={c}>
                                  <Tooltip title={c} placement="left">
                                    {c === '1 only' ? (
                                      <LooksOneIcon />
                                    ) : c === '2 only' ? (
                                      <LooksTwoIcon />
                                    ) : c === '1 then 2' ? (
                                      <>
                                        <LooksOneIcon />
                                        <ArrowRightIcon />
                                        <LooksTwoIcon />
                                      </>
                                    ) : c === '2 then 1' ? (
                                      <>
                                        <LooksTwoIcon />
                                        <ArrowRightIcon />
                                        <LooksOneIcon />
                                      </>
                                    ) : null}
                                  </Tooltip>
                                </ToggleButton>
                              ))}
                            </ToggleButtonGroup>
                            {/* <ToggleButtonGroup
                        value={leaderboardClue}
                        exclusive
                        onChange={handleLeaderboardClueToggle}
                        size="small"
                      >
                        <ToggleButton value="standard" aria-label="standard">
                          <Typography variant="caption">Standard</Typography>
                        </ToggleButton>
                        <ToggleButton
                          value="no-letters"
                          aria-label="no-letters"
                        >
                          <Typography variant="caption">No letters</Typography>
                        </ToggleButton>
                        <ToggleButton value="no-blanks" aria-label="no-blanks">
                          <Typography variant="caption">No blanks</Typography>
                        </ToggleButton>
                      </ToggleButtonGroup> */}
                          </Stack>
                          <Box>{getLineChart()}</Box>
                        </Stack>
                      </Stack>
                    </Stack>
                  ) : (
                    <Typography>No data</Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </StyledPaperItem>
          </Grid>
          <Grid item xs={12} sm={12} className="LEADERBOARD">
            <StyledPaperItem>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Typography
                    width={'100%'}
                    textAlign={'center'}
                    fontWeight={600}
                    letterSpacing={4}
                  >
                    TOP 50 LEADERBOARD
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <Stack direction={'row'} spacing={4}>
                      <ToggleButtonGroup
                        value={leaderboardOrder}
                        exclusive
                        onChange={handleLeaderboardOrderToggle}
                        size="small"
                      >
                        {starterKit.orders?.map((o) => (
                          <ToggleButton value={o} aria-label={o}>
                            <Tooltip title={o} placement="top">
                              {o === 'random' ? (
                                <CasinoIcon />
                              ) : o === 'alphabetical' ? (
                                <SortByAlphaIcon />
                              ) : o === 'sequence' ? (
                                <FormatListNumberedIcon />
                              ) : o === 'group' ? (
                                <LayersIcon />
                              ) : null}
                            </Tooltip>
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                      <ToggleButtonGroup
                        value={leaderboardClue}
                        exclusive
                        onChange={handleLeaderboardClueToggle}
                        size="small"
                      >
                        {starterKit.clues
                          ?.filter((c) => !['1 then 2', '2 then 1'].includes(c))
                          .map((c) => (
                            <ToggleButton value={c} aria-label={c}>
                              <Tooltip title={c} placement="top">
                                {c === '1 only' ? (
                                  <LooksOneIcon />
                                ) : c === '2 only' ? (
                                  <LooksTwoIcon />
                                ) : null}
                              </Tooltip>
                            </ToggleButton>
                          ))}
                      </ToggleButtonGroup>
                      {/* <ToggleButtonGroup
                        value={leaderboardClue}
                        exclusive
                        onChange={handleLeaderboardClueToggle}
                        size="small"
                      >
                        <ToggleButton value="standard" aria-label="standard">
                          <Typography variant="caption">Standard</Typography>
                        </ToggleButton>
                        <ToggleButton
                          value="no-letters"
                          aria-label="no-letters"
                        >
                          <Typography variant="caption">No letters</Typography>
                        </ToggleButton>
                        <ToggleButton value="no-blanks" aria-label="no-blanks">
                          <Typography variant="caption">No blanks</Typography>
                        </ToggleButton>
                      </ToggleButtonGroup> */}
                    </Stack>
                    <StyledPaperItem>
                      <Stack direction="row">{leaderboardTable()}</Stack>
                    </StyledPaperItem>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </StyledPaperItem>
          </Grid>
          <Grid item xs={12} sm={12} className="OPTIONS">
            <StyledPaperItem>
              <Stack direction={'row'} spacing={2}>
                <Stack
                  spacing={2}
                  sx={{
                    width: '45%',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="h6">Order by</Typography>
                  <FormControl sx={{ mb: 2 }}>
                    <RadioGroup
                      value={order}
                      onChange={(e) =>
                        handleOrderChange(e, e.target.value as Order)
                      }
                    >
                      {starterKit.orders?.map((o) => (
                        <FormControlLabel
                          value={o}
                          control={<Radio />}
                          label={capitalizeFirstLetter(o)}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Stack>
                <Box
                  sx={{
                    width: '10%',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Divider orientation="vertical" flexItem />
                </Box>
                <Stack
                  spacing={2}
                  sx={{
                    width: '45%',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="h6">Clues</Typography>
                  <Stack direction={'row'} spacing={2}>
                    <Typography variant="caption">
                      Clue 1: {getClueLabels(starterKit.starterKitSet, 1)}
                    </Typography>
                    {getClueLabels(starterKit.starterKitSet, 2) ? (
                      <>
                        <Typography variant="caption">|</Typography>
                        <Typography variant="caption">
                          Clue 2: {getClueLabels(starterKit.starterKitSet, 2)}
                        </Typography>
                      </>
                    ) : null}
                  </Stack>
                  <FormControl sx={{ mb: 2 }}>
                    <RadioGroup
                      value={clue}
                      onChange={(e) =>
                        handleClueChange(e, e.target.value as Clue)
                      }
                    >
                      {starterKit.clues?.map((o) => (
                        <FormControlLabel
                          value={o}
                          control={<Radio />}
                          label={o}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Stack>
                {/* <Box
                  sx={{
                    width: '10%',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Divider orientation="vertical" flexItem />
                </Box>
                <Stack
                  spacing={2}
                  sx={{
                    width: '45%',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="h6">Difficulty</Typography>
                  <FormControl sx={{ mb: 2 }}>
                    <RadioGroup
                      value={clue}
                      onChange={(e) =>
                        handleClueChange(e, e.target.value as Clue)
                      }
                    >
                      <FormControlLabel
                        value="standard"
                        control={<Radio />}
                        label="Standard"
                      />
                      <FormControlLabel
                        value="no-letters"
                        control={<Radio />}
                        label="No letters"
                      />
                      <FormControlLabel
                        value="no-blanks"
                        control={<Radio />}
                        label="No blanks"
                      />
                    </RadioGroup>
                  </FormControl>
                </Stack> */}
              </Stack>
            </StyledPaperItem>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="text" fullWidth onClick={close}>
          Cancel
        </Button>
        <Button
          variant="contained"
          fullWidth
          onClick={handleStarterKitPlay}
          disabled={maintenance.scheduled}
        >
          PLAY {starterKit.starterKitName}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
